import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { Calendar, Clock, ArrowLeft, Sparkles, Tag } from "lucide-react";
import { LeadMagnetSection } from "../components/LeadMagnetSection";
import { useSEO } from "../hooks/useSEO";

const CATEGORY_COLORS = {
  "Hiragana & Katakana": "#34D399",
  "JLPT": "#E94A3F",
  "Débutant": "#FFD166",
  "Kanji": "#1A2A40",
  "Voyage": "#06B6D4",
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

// ----- BLOG INDEX -----
export const BlogIndex = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setArticles(r.data.data || [])).finally(() => setLoading(false));
  }, []);

  useSEO({
    title: "Blog Japonais Pas à Pas : Conseils pour apprendre le japonais & réussir le JLPT",
    description: "Conseils, méthodes et astuces pour apprendre le japonais en autodidacte : hiragana, katakana, kanji, grammaire, JLPT N5 → N3. Articles écrits par notre équipe.",
    canonical: "https://japonais-pas-a-pas.com/blog",
  });

  return (
    <div className="washi-bg min-h-screen">
      <Navbar />

      <header className="max-w-5xl mx-auto px-6 sm:px-12 pt-16 pb-12">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1.5 rounded-full border border-[#1A2A40]/15 text-xs font-bold tracking-widest text-[#E94A3F] mb-5">
          <Sparkles className="w-3.5 h-3.5" /> NOTRE BLOG
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A2A40] leading-[1.05] mb-4">
          Conseils, astuces et <span className="text-[#E94A3F]">méthodes</span> pour apprendre le japonais
        </h1>
        <p className="text-base sm:text-lg text-[#4A5568] max-w-3xl">
          Articles écrits par <strong>Alexis & Atsuko</strong>, francophone passionné et native japonaise.
          Méthodes concrètes, pas de blabla.
        </p>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-12 pb-20">
        {loading ? (
          <div className="text-center py-16 text-[#A0AEC0]">Chargement…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="blog-grid">
            {articles.map((a, i) => {
              const color = CATEGORY_COLORS[a.category] || "#A0AEC0";
              return (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="bg-white rounded-2xl border border-[#1A2A40]/10 p-6 hover-lift shadow-brut-sm transition-shadow flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded-full text-white"
                      style={{ background: color }}
                    >
                      {a.category}
                    </span>
                    {i === 0 && (
                      <span className="text-[10px] font-black tracking-widest uppercase text-[#E94A3F]">★ MIS EN AVANT</span>
                    )}
                  </div>
                  <h2 className="font-display font-black text-xl text-[#1A2A40] leading-tight mb-3 group-hover:text-[#E94A3F]">
                    {a.title}
                  </h2>
                  <p className="text-sm text-[#4A5568] leading-relaxed flex-1 mb-5">{a.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-[#A0AEC0]">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" /> {formatDate(a.published_at)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {a.reading_time_min} min
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

// ----- BLOG ARTICLE -----
export const BlogArticle = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => nav("/blog", { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, nav]);

  useSEO({
    title: article ? `${article.title} : Japonais Pas à Pas` : undefined,
    description: article?.meta_description,
    canonical: article ? `https://japonais-pas-a-pas.com/blog/${article.slug}` : undefined,
  });

  useEffect(() => {
    if (!article) return;
    // JSON-LD Article schema
    const existing = document.getElementById("article-jsonld");
    if (existing) existing.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.meta_description,
      "datePublished": article.published_at,
      "author": [
        { "@type": "Person", "name": "Alexis Gresoviac" },
        { "@type": "Person", "name": "Atsuko Gresoviac" }
      ],
      "publisher": {
        "@type": "Organization",
        "name": "Japonais Pas à Pas",
        "logo": { "@type": "ImageObject", "url": "https://japonais-pas-a-pas.com/icon-512.png" }
      },
      "inLanguage": "fr-FR",
      "keywords": (article.keywords || []).join(", "),
      "mainEntityOfPage": `https://japonais-pas-a-pas.com/blog/${article.slug}`
    });
    document.head.appendChild(ld);
    return () => {
      const el = document.getElementById("article-jsonld");
      if (el) el.remove();
    };
  }, [article]);

  if (loading) return <div className="washi-bg min-h-screen"><Navbar /><div className="text-center py-20 text-[#A0AEC0]">Chargement…</div></div>;
  if (!article) return null;

  const color = CATEGORY_COLORS[article.category] || "#A0AEC0";

  return (
    <div className="washi-bg min-h-screen">
      <Navbar />

      <header className="max-w-3xl mx-auto px-6 sm:px-12 pt-12 pb-8" data-testid="article-header">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#E94A3F] mb-8">
          <ArrowLeft className="w-4 h-4" /> Retour au blog
        </Link>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span
            className="text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded-full text-white"
            style={{ background: color }}
          >
            <Tag className="w-3 h-3 inline mr-1 -mt-0.5" /> {article.category}
          </span>
          <span className="text-xs text-[#A0AEC0] inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> {formatDate(article.published_at)}
          </span>
          <span className="text-xs text-[#A0AEC0] inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {article.reading_time_min} min de lecture
          </span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A2A40] leading-[1.1] mb-5">
          {article.title}
        </h1>
        <div className="flex items-center gap-3 text-sm text-[#4A5568]">
          <div className="flex -space-x-2">
            <div className="w-9 h-9 rounded-full bg-[#E94A3F] text-white flex items-center justify-center font-bold text-xs border-2 border-white">A</div>
            <div className="w-9 h-9 rounded-full bg-[#1A2A40] text-white flex items-center justify-center font-bold text-xs border-2 border-white font-jp">あ</div>
          </div>
          Par <strong>Alexis & Atsuko</strong>
        </div>
      </header>

      <article
        className="blog-content max-w-3xl mx-auto px-6 sm:px-12 pb-16"
        data-testid="article-content"
        dangerouslySetInnerHTML={{ __html: article.content_html }}
      />

      {/* Lead magnet on relevant articles (voyage / 10 erreurs) */}
      {(article.slug === "voyager-japon-50-phrases-utiles" || article.slug === "10-erreurs-debutants-japonais") && (
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <LeadMagnetSection compact />
        </div>
      )}

      {/* CTA at end */}
      <div className="max-w-3xl mx-auto px-6 sm:px-12 pb-20">
        <div className="bg-[#1A2A40] text-white rounded-3xl p-8 sm:p-10 shadow-brut">
          <div className="font-display font-black text-2xl sm:text-3xl mb-3 leading-tight">
            Envie de mettre en pratique ?
          </div>
          <p className="text-white/80 mb-6 leading-relaxed">
            Notre méthode complète : 200 leçons interactives, tuteur IA, audio natif, examens blancs JLPT.
            <strong> 3 jours d'essai gratuit, sans CB.</strong>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> Commencer gratuitement
            </Link>
            <Link to="/blog" className="border-2 border-white/30 hover:border-white/60 text-white font-bold py-3 px-6 rounded-xl">
              ← Plus d'articles
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogIndex;
